import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { ModeId, Theme, ThemeFamilyId, ThemeVariant } from './contract.js';
import { tokensToCssVars } from './contract.js';
import { getTheme, THEMES } from './registry.js';
import { systemMode } from './firstPaint.js';

/**
 * Runtime override for a driver-owned skin. ThemeProvider writes
 * these CSS vars AFTER the variant tokens, so the skin's accents
 * (and optional surface / bg for skins whose identity requires a
 * shifted page background) layer on top of the base family without
 * disturbing typography, radii, sidebar tokens, or motion.
 *
 * `themeId` switches the base family (e.g. Obsidian composes over
 * Luce; Midnight / Sunset / Neon over Default). `mode` is NOT a
 * settable override any more — the driver's Light/Dark toggle is
 * sovereign, and the resolver (`useSkinSync`) picks the matching
 * light/dark variant block from the skin's metadata.
 *
 * None of these values persist to localStorage — the override
 * flips off the instant the driver un-applies the skin.
 */
export interface SkinOverride {
  themeId?: ThemeFamilyId;
  accentColor?: string;
  chargingColor?: string;
  /** Override --evi-surface. Leave unset to inherit the base variant. */
  surface?: string;
  /** Override --evi-bg. Leave unset to inherit the base variant. */
  bg?: string;
  /**
   * Override --evi-border. Used by skins whose surface is a whole tonal
   * step away from the base family (e.g. Bloom's pastel peach — the
   * base default-light's crisper `#E5E4DE` reads wrong against peach).
   */
  border?: string;
  /**
   * Override --evi-text-mute. Same scenario — skins with distinctive
   * surface tones may need a muted text colour that reads against that
   * surface rather than the base family's muted.
   */
  textMute?: string;
}

interface ThemeContextValue {
  theme: Theme;
  mode: ModeId;
  variant: ThemeVariant;
  setThemeId: (id: ThemeFamilyId) => void;
  setMode: (mode: ModeId) => void;
  /** Runtime skin override (driver-purchased). Null disables. */
  setSkinOverride: (skin: SkinOverride | null) => void;
  skinOverride: SkinOverride | null;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
  initialThemeId?: ThemeFamilyId;
  initialMode?: ModeId;
  storageThemeKey?: string;
  storageModeKey?: string;
  queryThemeParam?: string;
  queryModeParam?: string;
  /**
   * The driver app (Warren, 21 October 2026): with no saved choice, follow the device's light or dark setting (live),
   * light when the device reports none; save the mode only when the driver chooses it (setMode). Off: the original
   * behaviour (default dark, the resolved mode saved on every load), which Admin keeps.
   */
  followSystemMode?: boolean;
}

/**
 * Resolves the active `(theme, mode)` tuple from query param ▸ localStorage
 * ▸ initial prop ▸ `default` / `dark`, paints the variant's tokens on
 * `:root`, and exposes the result via `useTheme()`.
 *
 * The two axes persist independently so flipping mode keeps the family
 * and flipping family keeps the mode.
 */
export function ThemeProvider({
  children,
  initialThemeId,
  initialMode,
  storageThemeKey = 'evi.theme',
  storageModeKey = 'evi.mode',
  queryThemeParam = 'theme',
  queryModeParam = 'mode',
  followSystemMode = false,
}: ThemeProviderProps) {
  const [themeId, setThemeIdState] = useState<ThemeFamilyId>(() => {
    if (typeof window === 'undefined') return initialThemeId ?? 'default';
    const url = new URL(window.location.href);
    const q = url.searchParams.get(queryThemeParam);
    // Driver Themes ticket — registry-driven lookup replaces the
    // legacy hardcoded ['default', 'luce'] allow-list. Without this,
    // setThemeId could change the React state to a non-default,
    // non-luce family in-session but the next page boot would reject
    // the localStorage rehydration and reset the user. The `in
    // THEMES` check accepts any family the registry knows about
    // (today: default, luce, cartographic, neon, electric-light,
    // dark-electric, obsidian).
    if (q && q in THEMES) return q as ThemeFamilyId;
    const s = window.localStorage.getItem(storageThemeKey);
    if (s && s in THEMES) return s as ThemeFamilyId;
    return initialThemeId ?? 'default';
  });

  const [mode, setModeState] = useState<ModeId>(() => {
    if (typeof window === 'undefined') return initialMode ?? 'dark';
    const url = new URL(window.location.href);
    const q = url.searchParams.get(queryModeParam);
    if (q === 'light' || q === 'dark') return q;
    const s = window.localStorage.getItem(storageModeKey);
    if (s === 'light' || s === 'dark') return s;
    if (followSystemMode) return systemMode(window.matchMedia?.bind(window));
    return initialMode ?? 'dark';
  });
  // A choice (saved, or forced by the query) is sovereign; without one the mode follows the device.
  const [initiallyChosen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const q = new URL(window.location.href).searchParams.get(queryModeParam);
    const s = window.localStorage.getItem(storageModeKey);
    return q === 'light' || q === 'dark' || s === 'light' || s === 'dark';
  });
  const chosenRef = useRef<boolean>(initiallyChosen);

  const [skinOverride, setSkinOverrideState] = useState<SkinOverride | null>(null);

  // Effective theme family: the skin override wins (unlocks are curated
  // for a specific base palette).
  const effectiveThemeId = skinOverride?.themeId ?? themeId;
  const theme = useMemo(() => getTheme(effectiveThemeId), [effectiveThemeId]);

  // Effective mode: the driver's saved choice, CLAMPED to a mode the
  // active family actually supports. A single-mode family (availableModes
  // set, e.g. ['dark']) sitting on the other saved mode would otherwise
  // resolve theme.variants[<unsupported>] — the retired variant. When
  // availableModes is absent (every family today) this is a no-op, so
  // effectiveMode === mode. The clamp is display-only: the raw `mode`
  // state is untouched, so switching back to a both-modes family restores
  // the driver's saved preference. The first entry in availableModes is
  // the fallback when the saved mode isn't supported. The skin override
  // can no longer force mode either; only availableModes can clamp it.
  const effectiveMode =
    theme.availableModes && !theme.availableModes.includes(mode)
      ? theme.availableModes[0] ?? mode
      : mode;

  const variant = theme.variants[effectiveMode];

  // Disney-12 #4 "Straight-Ahead Action" — theme switches feel
  // continuous rather than snap-instant. On every CHANGE (not the
  // initial paint), we toggle a transient `evi-theme-transitioning`
  // class on <html> for the transition window. The class drives a
  // global `transition` rule on commonly-themed properties
  // (background-color / color / border-color / fill / stroke) — see
  // packages/skins/src/styles.css. After ~320ms the class clears so
  // the transitions don't fight non-theme interactions later.
  const firstPaintRef = useRef(true);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    // First-paint skip — on mount the variant tokens land on :root
    // for the FIRST time and would trigger transitions from the
    // (then-unset) initial values, which reads as a flash. Skip the
    // transition class until the second-onward effect run.
    if (!firstPaintRef.current) {
      root.classList.add('evi-theme-transitioning');
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = setTimeout(() => {
        root.classList.remove('evi-theme-transitioning');
        transitionTimerRef.current = null;
      }, 320);
    }
    firstPaintRef.current = false;
    const vars = tokensToCssVars(variant.tokens);
    for (const [k, v] of Object.entries(vars)) {
      if (v === '') root.style.removeProperty(k);
      else root.style.setProperty(k, v);
    }
    // Skin override CSS-vars land AFTER the variant tokens so they
    // win at the cascade layer. --evi-charging drives GlowRing +
    // MiniChargingBar, --evi-accent drives CTAs + progress fills.
    // --evi-surface / --evi-bg override when the skin's identity
    // requires shifting the page background (e.g. Midnight-light's
    // pale icy blue, Neon-light's soft pink).
    const skinBg = skinOverride?.bg;
    if (skinOverride?.accentColor) {
      root.style.setProperty('--evi-accent', skinOverride.accentColor);
    }
    if (skinOverride?.chargingColor) {
      root.style.setProperty('--evi-charging', skinOverride.chargingColor);
    }
    if (skinOverride?.surface) {
      root.style.setProperty('--evi-surface', skinOverride.surface);
    }
    if (skinBg) {
      root.style.setProperty('--evi-bg', skinBg);
    }
    if (skinOverride?.border) {
      root.style.setProperty('--evi-border', skinOverride.border);
    }
    if (skinOverride?.textMute) {
      root.style.setProperty('--evi-text-mute', skinOverride.textMute);
    }
    root.dataset['theme'] = theme.id;
    root.dataset['mode'] = effectiveMode;
    root.style.colorScheme = effectiveMode;
    root.style.backgroundColor = skinBg ?? variant.tokens.colors.bg;
    root.style.color = variant.tokens.colors.text;
    root.style.fontFamily = variant.tokens.fonts.sans;
  }, [theme, effectiveMode, variant, skinOverride]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Only persist the driver's own preference — NOT the skin
    // override. Un-applying the skin should reveal the driver's
    // saved choice intact.
    window.localStorage.setItem(storageThemeKey, themeId);
  }, [themeId, storageThemeKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || followSystemMode) return;
    window.localStorage.setItem(storageModeKey, mode);
  }, [mode, storageModeKey, followSystemMode]);

  // Following the device: a light/dark switch on the device moves the app while the driver has not chosen.
  useEffect(() => {
    if (typeof window === 'undefined' || !followSystemMode || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: { matches: boolean }) => { if (!chosenRef.current) setModeState(e.matches ? 'dark' : 'light'); };
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, [followSystemMode]);

  // The driver's own choice: saved, and sovereign from then on.
  const setMode = useMemo(() => (next: ModeId) => {
    setModeState(next);
    if (!followSystemMode || typeof window === 'undefined') return;
    chosenRef.current = true;
    window.localStorage.setItem(storageModeKey, next);
  }, [followSystemMode, storageModeKey]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      mode: effectiveMode,
      variant,
      setThemeId: setThemeIdState,
      setMode,
      setSkinOverride: setSkinOverrideState,
      skinOverride,
    }),
    [theme, effectiveMode, variant, skinOverride, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
